/* External */
import {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { Set } from 'immutable';
import { useDispatch } from 'react-redux';

/* Utils */
import { STATUS_TYPES } from '../utils/constants';
import useIsFetchingChannels from './useIsFetchingChannels';
import useMultipleRequests from './useMultipleRequests';
import { scheduleFetchChannelSchedules } from '../storage/channels';
import { deleteMultipleRequests } from '../storage/requests';
import useRefSync from './useRefSync';

export default function useFetchAvailableSchedules({
  channels, numberOfSchedules, sourceProvider = '',
}) {
  const [requestIds, setRequestIds] = useState(() => Set());
  const addRequestId = useCallback((requestId) => {
    setRequestIds((currentRequestIds) => (currentRequestIds.add(requestId)));
  }, []);
  const requestIdsRef = useRefSync(requestIds);
  const requests = useMultipleRequests(requestIds);

  const alreadyFetched = useRef();
  const isFetchingChannels = useIsFetchingChannels();

  const getAlreadyFetched = useCallback(() => {
    if (!alreadyFetched.current) {
      alreadyFetched.current = Set();
    }
    return alreadyFetched.current;
  }, []);

  const isFetchingSchedules = useMemo(() => (
    requests.some(({ status }) => status === STATUS_TYPES.INIT_STATE)
      || !!channels?.some(({ channelId }) => !getAlreadyFetched().has(channelId))
  ), [requests, channels, getAlreadyFetched]);

  const dispatch = useDispatch();
  useEffect(() => {
    const newChannelIds = channels?.toList().map(
      (channel) => channel.channelId,
    ).filter(
      (channelId) => !getAlreadyFetched().has(channelId),
    );

    if (newChannelIds && newChannelIds.size > 0) {
      const action = scheduleFetchChannelSchedules({
        channelIdOrIds: newChannelIds,
        schedulesForChannel: numberOfSchedules,
        sourceProvider,
      });
      addRequestId(action.requestId);
      dispatch(action);
      alreadyFetched.current = getAlreadyFetched().union(newChannelIds);
    }
  }, [
    numberOfSchedules,
    getAlreadyFetched,
    sourceProvider,
    channels,
    addRequestId,
    dispatch,
  ]);

  useEffect(() => () => {
    dispatch(deleteMultipleRequests(requestIdsRef.current));
  }, [dispatch, requestIdsRef]);

  return {
    isFetchingChannels,
    isFetchingSchedules,
  };
}
